import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import 'firebase/auth';
import {
    browserLocalPersistence,
    getAuth,
    setPersistence,
} from 'firebase/auth';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import {
    forkJoin,
    from,
    lastValueFrom,
    Observable,
    of,
    Subject,
    throwError,
} from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import {
    EMPLOYEES_COLLECTION,
    GET_COMPANY_LEAD_BY_RESET_PASSWORD_KEY,
    GET_EMPLOYEE_BY_RESET_PASSWORD_KEY,
    HELPER_EMPLOYEE_ID_TARGET_STORAGE_KEY,
    READY_FOR_CONSULTATION_PROCESS,
    USER_LOCAL_STORAGE_KEY,
    USERS_COLLECTION,
} from '../../app.constants';
import { CopHomeService } from '../../modules/cop/cop-home/cop-home.service';
import { CopStoreService } from '../../modules/cop/cop-home/steps/cop-store/cop-store.service';
import { LoggedInUser } from '../../shared/models/user.model';
import { AlertComponent } from '../components/alert/alert.component';
import { FireLoggingService } from './fire-logging.service';
import { StorageService } from './storage.service';
import { UserService } from './user.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FinsteinHelperTypesEnum } from '../../shared/enums/finstein-helper-types.enum';
@Injectable({
    providedIn: 'root',
})
export class AuthFirebaseService implements OnDestroy {
    private loggedOutSubject = new Subject<boolean>();
    loggedOut$ = this.loggedOutSubject.asObservable();

    constructor(
        private userService: UserService,
        public afAuth: AngularFireAuth,
        private storageService: StorageService,
        private functions: AngularFireFunctions,
        private firebaseDb: AngularFireDatabase,
        private firestore: AngularFirestore,
        private dialog: MatDialog,
        private spinner: NgxSpinnerService,
        private router: Router,
        private ngZone: NgZone,
        private fireLogging: FireLoggingService,
        private copHomeService: CopHomeService,
        private copStoreService: CopStoreService
    ) {}

    ngOnDestroy(): void {}

    signIn(data: any, customUserValidationFn?: any): Observable<any> {
        if (this.storageService.get(USER_LOCAL_STORAGE_KEY)) {
            this.signOut().subscribe();
        }
        return new Observable((observer) => {
            return from(setPersistence(getAuth(), browserLocalPersistence))
                .pipe(
                    concatMap(() =>
                        this.afAuth.signInWithEmailAndPassword(
                            data.email.toLowerCase(),
                            data.password
                        )
                    ),
                    switchMap((success) => {
                        return this.determineUserProfile(success);
                    }),
                    switchMap((success) => {
                        return this.userService.listenForChangesAtUser(
                            success.profile === 'USER'
                                ? USERS_COLLECTION
                                : EMPLOYEES_COLLECTION,
                            success.data.user?.uid
                        );
                    }),
                    switchMap((success) => {
                        return this.setUserSession(success);
                    }),
                    switchMap((success) => {
                        return from(this.listenUserSessionChanged(success));
                    }),
                    concatMap((loggedInUser) => {
                        if (
                            loggedInUser.status ===
                            READY_FOR_CONSULTATION_PROCESS
                        ) {
                            return this.copHomeService
                                .updateEmployeeStatusToConsultancyProcessStarted()
                                .pipe(map(() => loggedInUser));
                        }

                        return of(loggedInUser);
                    }),
                    catchError((err) => {
                        observer.error(err);
                        return this.signOut().pipe(
                            concatMap(() => throwError(err))
                        );
                    })
                )
                .subscribe((loggedInUser) => {
                    if (customUserValidationFn) {
                        customUserValidationFn(loggedInUser, observer);
                    } else {
                        observer.next();
                    }
                });
        });
    }

    requestResetPassword(resetPasswordFunction: string, email: string) {
        return this.functions
            .httpsCallable(resetPasswordFunction)({ email })
            .pipe(
                catchError((error) => {
                    const message = error.error
                        ? error.error.message
                        : error.message;
                    this.fireLogging
                        .sendErrorLog(`An error occurred calling the function
                ${resetPasswordFunction}. Email: ${email}, details: ${
                        message || JSON.stringify(error)
                    }`);
                    return of(null);
                })
            );
    }

    getEmployeeByResetPasswordKey(key: string) {
        return this.functions
            .httpsCallable(GET_EMPLOYEE_BY_RESET_PASSWORD_KEY)({ key })
            .pipe(
                catchError((error) => {
                    const message = error.error
                        ? error.error.message
                        : error.message;
                    this.fireLogging
                        .sendErrorLog(`An error occurred calling the function
                ${GET_EMPLOYEE_BY_RESET_PASSWORD_KEY}. Key: ${key}, details: ${
                        message || JSON.stringify(error)
                    }`);
                    return of(null);
                })
            );
    }

    getCompanyLeadByResetPasswordKey(key: string) {
        return this.functions
            .httpsCallable(GET_COMPANY_LEAD_BY_RESET_PASSWORD_KEY)({ key })
            .pipe(
                catchError((error) => {
                    const message = error.error
                        ? error.error.message
                        : error.message;
                    this.fireLogging
                        .sendErrorLog(`An error occurred calling the function
                ${GET_COMPANY_LEAD_BY_RESET_PASSWORD_KEY}. Key: ${key},
                details: ${message || JSON.stringify(error)}`);
                    return of(null);
                })
            );
    }

    changePassword(
        resetPasswordFunction: string,
        email: string,
        password: string
    ) {
        return this.functions
            .httpsCallable(resetPasswordFunction)({
                email,
                password,
            })
            .pipe(
                catchError((error) => {
                    const message = error.error
                        ? error.error.message
                        : error.message;
                    this.fireLogging
                        .sendErrorLog(`An error occurred calling the function
                ${resetPasswordFunction}. Email: ${email}, details: ${
                        message || JSON.stringify(error)
                    }`);
                    return of(null);
                })
            );
    }

    signOut() {
        this.loggedOutSubject.next(true);

        return from(this.afAuth.signOut()).pipe(
            tap(() => {
                this.closeUserSessionListener();
                this.userService.destroyAllUserObservables();
                this.storageService.remove(USER_LOCAL_STORAGE_KEY);
                this.storageService.remove(HELPER_EMPLOYEE_ID_TARGET_STORAGE_KEY);
                this.userService.loggedUser = new LoggedInUser();
                this.userService.operatedByHelperUser = false;
                this.copStoreService.storeCart = null;
            })
        );
    }

    public setUserSession(user: LoggedInUser) {
        if (this.userService.browserClientData) {
            try {
                return from(
                    this.firebaseDb.database
                        .ref(`website/user-sessions/${user.id}`)
                        .set({
                            ...this.userService.browserClientData,
                            createdAt: moment(new Date()).format(
                                'DD-MM-YYYY HH:mm:ss'
                            ),
                        })
                ).pipe(map(() => user));
            } catch (err) {
                throwError(err);
            }
        }
        return of(user);
    }

    async listenUserSessionChanged(user: LoggedInUser) {
        if (user.id) {
            const path = `website/user-sessions/${user.id}`;
            this.firebaseDb.database.ref(path).off('value');
            this.firebaseDb.database.ref(path).on(
                'value',
                (snapshot) => {
                    const val = snapshot.val();
                    if (
                        this.userService.browserClientData &&
                        val &&
                        (val.userAgent !== navigator.userAgent ||
                            val.ip !== this.userService.browserClientData.ip)
                    ) {
                        this.signOut().subscribe(() => {
                            this.ngZone.run(async () => {
                                await this.router.navigate(['/auth']);
                                await this.spinner.hide();
                                await this.dialog.closeAll();
                                this.dialog.open(AlertComponent, {
                                    panelClass: 'curved-modal',
                                    disableClose: false,
                                    data: {
                                        title: 'new-login-detected.title',
                                        text: 'new-login-detected.text',
                                        textBtnClose: 'system.close',
                                    },
                                });
                            });
                        });
                    }
                },
                (err) => {
                    this.fireLogging.sendAlertLog(
                        `Error when trying listenUserSessionChanged. Details: ${err.message}`
                    );
                }
            );
        }
        return user;
    }

    closeUserSessionListener() {
        if (this.userService.loggedUser) {
            const path = `website/user-sessions/${this.userService.loggedUser.id}`;
            this.firebaseDb.database.ref(path).off('value');
        }
    }

    determineUserProfile(
        data: any
    ): Observable<{ data: any; profile: 'EMPLOYEE' | 'USER' }> {
        const employeeDocObs = from(
            this.firestore
                .collection(EMPLOYEES_COLLECTION)
                .doc(data.user.uid)
                .get()
        ).pipe(
            catchError(() => of(null))
        );

        const userDocObs = from(
            this.firestore
                .collection(USERS_COLLECTION)
                .doc(data.user.uid)
                .get()
        ).pipe(
            catchError(() => of(null))
        );

        return forkJoin([employeeDocObs, userDocObs]).pipe(
            map(([employeeSnapshot, userSnapshot]) => {
                const userDoc: any = userSnapshot?.data();
                if (
                    userSnapshot?.exists &&
                    userDoc.finsteinHelper &&
                    [
                        FinsteinHelperTypesEnum.COP_HELPER,
                        FinsteinHelperTypesEnum.GLOBAL_HELPER,
                        FinsteinHelperTypesEnum.BROKER_HELPER
                    ].includes(userDoc.finsteinHelperType)
                ) {
                    return { data, profile: 'USER' };
                }

                if (employeeSnapshot?.exists) {
                    return { data, profile: 'EMPLOYEE' };
                }

                throw new Error('User not found in either collection.');
            })
        );
    }
}
